import { action, computed, makeObservable, observable } from 'mobx';

import { requireService } from 'src/packages/di';
import { ComputedDataSource } from 'src/packages/template-builder/TemplateBuilder';

import type { WellCollapseEntity } from './WellCollapse';
import type { TFilterDataSource } from '../filter-widget/WellsFilterDataSource';
import type { WellCollapseItemEntity } from '../well-collapse/components/well-collapse-item/WellCollapseItem.entity';
import type { ObservableSet } from 'mobx';
import type { TWellListView } from 'src/api-types/wells.types';
import type { IDataSource } from 'src/packages/template-builder/TemplateBuilder';

interface WellListWidgetOptions {
  filterState: TFilterDataSource | null;
  filterBy: number | null;
  groupBy: number | null;
  searchValue: string | null;
  selectedWellID: number | null;
  collapsedKeys: string[] | null;
  sorting: number | null;
  groupId: string | null;
}

export class WellListPanelEntity {
  @observable wells: WellCollapseEntity[] = [];
  @observable view: TWellListView;
  @observable filterState: TFilterDataSource | null;
  @observable filterBy: number;
  @observable groupBy: number;
  @observable searchValue: string;
  @observable selectedWellID: number | null;
  @observable collapsedKeys: ObservableSet<string>;
  @observable sorting: number;
  @observable groupId: string | null = null;

  constructor(
    {
      filterState,
      filterBy,
      groupBy,
      searchValue,
      selectedWellID,
      collapsedKeys,
      sorting,
      groupId,
    }: WellListWidgetOptions,
    private readonly preloadService = requireService('preloadService'),
    private readonly userPreferencesService = requireService('userPreference')
  ) {
    const view = this.preloadService.getPreloadedData<TWellListView>('well-list-control');

    this.collapsedKeys = observable.set(collapsedKeys ?? []);
    if (groupBy) {
      this.groupBy = groupBy;
    } else {
      const index = view.grouping.groups.findIndex(
        (group) => group.fieldId === this.userPreferencesService.preferences.defaultWellListGroup
      );
      this.groupBy = index > -1 ? index : view.grouping.defaultGroupingIndex;
    }
    if (filterBy) {
      this.filterBy = filterBy;
    } else {
      const index = view.filtering.filters.findIndex(
        (filter) => filter.name === this.userPreferencesService.preferences.defaultWellListFilter
      );
      this.filterBy = index > -1 ? index : view.grouping.defaultGroupingIndex;
    }

    this.filterState = filterState;
    this.searchValue = searchValue ?? '';
    this.selectedWellID = selectedWellID;
    this.view = view;
    this.sorting = sorting ?? view.sorting.defaultSortingIndex;
    this.groupId = groupId;

    makeObservable(this);
  }

  @computed
  get wellId(): number | null {
    return this.selectedWellID;
  }

  @action.bound
  setGroup(group: string | null): void {
    this.groupId = group;
  }

  getNameT(): string {
    return 'wellList:title';
  }

  @action.bound
  updateWellStatus(id: number, status: string): void {
    for (const wellGroup of this.wells) {
      for (const well of wellGroup.items) {
        if (well.id === id) {
          well.setStatus(status);

          return;
        }
      }
    }
  }

  @action.bound
  setCollapsedKeys(keys: string[] | undefined): void {
    this.collapsedKeys = observable.set(keys ?? []);
  }

  @action.bound
  addCollapsedKey(key: string): void {
    this.collapsedKeys.add(key);
  }

  @action.bound
  removeCollapsedKey(key: string): void {
    this.collapsedKeys.delete(key);
  }

  @action.bound
  setWellId(wellId: number | null): void {
    this.selectedWellID = wellId;
  }

  @action.bound
  changeSorting(sorting: number): void {
    this.sorting = sorting;
  }

  @action.bound
  setOuterState(state: TFilterDataSource | null): void {
    this.filterState = state;
  }

  createDataSource(): IDataSource {
    return new ComputedDataSource(() => ({
      search: this.searchValue,
      ...this.filterState,
      sorting: this.sorting,
    }));
  }

  @action.bound
  changeFilterBy(filter: number): void {
    this.filterBy = filter;
  }

  @action.bound
  changeGroupBy(groupBy: number): void {
    this.groupBy = groupBy;
  }

  @action.bound
  changeSearchValue = (value: string) => {
    this.searchValue = value;
  };

  @action.bound
  setWells(wells: WellCollapseEntity[]): void {
    this.wells = wells;
  }

  @action.bound
  setSelectedWell(well: WellCollapseItemEntity | null): void {
    this.setWellId(well?.id ?? null);
  }
}
